import { startRegistration, startAuthentication } from '@simplewebauthn/browser';
import { signIn } from 'next-auth/react';

/**
 * Check if WebAuthn/Passkeys are supported in the current browser
 */
export function isPasskeySupported() {
    return (
        typeof window !== 'undefined' &&
        window.PublicKeyCredential !== undefined &&
        typeof window.PublicKeyCredential === 'function'
    );
}

/**
 * Check if platform authenticator (Touch ID, Face ID, Windows Hello) is available
 */
export async function isPlatformAuthenticatorAvailable() {
    if (!isPasskeySupported()) return false;

    try {
        return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
    } catch {
        return false;
    }
}

/**
 * Passkey client - wrapper for passkey operations
 * All operations proxy through the Next.js API to the Rails backend
 */
export const passkeyClient = {
    /**
     * Get registration options from backend
     * Requires user to be authenticated
     */
    async getRegistrationOptions() {
        const response = await fetch('/api/auth/passkey/registration-options');
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to get registration options');
        }
        return response.json();
    },

    /**
     * Register a new passkey
     * @param {string} name - Optional friendly name for the passkey
     */
    async registerPasskey(name) {
        // Get registration options from backend
        const options = await this.getRegistrationOptions();

        // Validate options before passing to WebAuthn
        if (!options || !options.challenge || !options.user) {
            throw new Error('Invalid registration options received from server');
        }

        // Start WebAuthn registration ceremony in browser
        let credential;
        try {
            credential = await startRegistration(options);
        } catch (error) {
            if (error.name === 'NotAllowedError') {
                throw error; // Re-throw cancellation
            }
            if (error.name === 'InvalidStateError') {
                throw new Error('This passkey is already registered to your account.');
            }
            throw new Error('Failed to create passkey. Please try again.');
        }

        // Send credential to backend for verification and storage
        const response = await fetch('/api/auth/passkey/register', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                credential,
                name: name || `Passkey ${new Date().toLocaleDateString()}`,
            }),
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to register passkey');
        }

        return response.json();
    },

    /**
     * Get authentication options for passkey login
     * @param {string} email - Optional email for conditional UI / account lookup
     */
    async getAuthenticationOptions(email) {
        const response = await fetch('/api/auth/passkey/authentication-options', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email }),
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to get authentication options');
        }

        return response.json();
    },

    /**
     * Authenticate with passkey and sign in via NextAuth
     * @param {string} email - Optional email for conditional UI
     * @param {Object} options - { redirect, callbackUrl }
     * @returns {Promise<{ok: boolean, error?: string}>}
     */
    async authenticateWithPasskey(email, options = {}) {
        const { redirect = true, callbackUrl = '/' } = options;

        try {
            // Get authentication options from backend
            const authOptions = await this.getAuthenticationOptions(email);

            // Extract challenge_key from response (backend requires this for verification)
            const { challenge_key, ...webAuthnOptions } = authOptions;

            // Start WebAuthn authentication ceremony in browser
            const credential = await startAuthentication(webAuthnOptions);

            // Send credential to backend for verification (include challenge_key)
            const response = await fetch('/api/auth/passkey/authenticate', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    challenge_key,
                    ...credential,
                }),
            });

            const data = await response.json();

            if (!response.ok || !data.ok) {
                return {
                    ok: false,
                    error: 'Login with Passkey failed. Please try again.',
                };
            }

            // Use the token to sign in via NextAuth
            // The NextAuth credentials provider accepts a 'token' parameter
            // which bypasses password auth and uses the existing JWT
            const signInResult = await signIn('credentials', {
                token: data.token,
                redirect,
                callbackUrl,
            });

            if (signInResult?.error) {
                return {
                    ok: false,
                    error: signInResult.error,
                };
            }

            return {
                ok: true,
            };
        } catch (error) {
            // Handle user cancellation or timeout
            if (error.name === 'NotAllowedError') {
                return {
                    ok: false,
                    error: 'Login with Passkey took too long or was cancelled. Please try again.',
                };
            }

            return {
                ok: false,
                error: 'Login with Passkey failed. Please try again.',
            };
        }
    },

    /**
     * List user's registered passkeys
     * Requires user to be authenticated
     */
    async listPasskeys() {
        const response = await fetch('/api/auth/passkey/list');

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to list passkeys');
        }

        return response.json();
    },

    /**
     * Delete a passkey
     * @param {string|number} id - Passkey ID to delete
     */
    async deletePasskey(id) {
        const response = await fetch(`/api/auth/passkey/${id}`, {
            method: 'DELETE',
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to delete passkey');
        }

        return response.json();
    },
};

export default passkeyClient;
